import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  FileText, Upload, CheckCircle, XCircle,
  AlertCircle, Download, Eye, Calendar,
  ChevronLeft, RefreshCw, X, FolderOpen,
  ClipboardList, Image,
  FileSignature, ClipboardCheck, ChevronDown, Maximize2,
} from 'lucide-react';
import { staffApi, StaffMe } from '../../services/staffApi';
import {
  staffPortal,
  staffDocumentTemplates,
  staffDocumentUploads,
} from '../../services/staffDocumentService';
import { toastService } from '../../services/toastService';
import { DatePicker } from '../../components/DatePicker';
import SearchableDropdown from '../../components/SearchableDropdown';
import { StaffPage, RUBY } from '../../components/staff/StaffChrome';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * MY PORTAL — the RubyTill INTERNAL STAFF member's own onboarding view.
 *
 * Port of platform-app's EmployeePortal into the super-admin panel, restyled
 * for the RUBY light chrome (black header bar + white cards on stone-100).
 * Differences from the source:
 *   - Identity comes from staffApi.me() (PlatformAdminUserPool), not the
 *     tenant Cognito session — all data routes are /me/* via staffPortal.*.
 *   - No POS PIN card, no tenant/package gates.
 *   - Notifications go through toastService (no inline banner, no alerts).
 *
 * Kept 1:1 from the source: workflow list -> workflow detail -> inline task
 * completion (upload, acknowledgment with typed signature + fullscreen
 * scroll-to-end policy modal, web form fill, fillable_doc download-blank /
 * upload-completed), expiry dates, rejected-state re-submission, progress
 * bars per workflow, plus a "My documents" archive with view links.
 */

// ---------------------------------------------------------------------------
// Local shapes — response conventions match platform-app's documentService.
// Defined here (not imported) so this page compiles independently of the
// sibling service module's own typing; call sites cast through `unknown`.
// ---------------------------------------------------------------------------

type DocumentRequestStatus = 'pending' | 'submitted' | 'verified' | 'rejected';
type DocumentTemplateType = 'upload' | 'acknowledgment' | 'form' | 'sheet' | 'fillable_doc';

interface FormField {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'checkbox' | 'date' | 'email' | 'phone' | 'number';
  required: boolean;
  options?: string[];
  placeholder?: string;
  order?: number;
}

interface DocumentTemplate {
  id: string;
  name: string;
  description?: string;
  category?: string;
  isRequired?: boolean;
  hasExpiry?: boolean;
  acceptedTypes?: string[];
  templateType?: DocumentTemplateType;
  disclaimerText?: string;
  formFields?: FormField[];
  referenceFileKey?: string;
  referenceFileName?: string;
}

interface DocumentRequest {
  id: string;
  documentTemplateId: string;
  templateName: string;
  category?: string;
  status: DocumentRequestStatus;
  onboardingTemplateId?: string;
  assignmentId?: string;
  requestedAt?: number;
  submittedAt?: number;
  verifiedAt?: number;
  rejectedAt?: number;
  rejectionReason?: string;
  documentId?: string;
  dueDate?: number;
  notes?: string;
  templateType?: DocumentTemplateType;
  allowedFileTypes?: string[];
  maxFileSize?: number;
  requiresExpiry?: boolean;
  acknowledgedAt?: number;
  signature?: string;
  formResponses?: Record<string, any>;
}

interface StaffDocument {
  id: string;
  requestId?: string;
  documentTemplateId?: string;
  documentName?: string;
  fileName: string;
  fileKey: string;
  fileType?: string;
  fileSize?: number;
  expiryDate?: number;
  uploadedAt?: number;
  status?: string;
}

interface StatusCounts {
  pending: number;
  submitted: number;
  verified: number;
  rejected: number;
}

/** Normalized workflow card — built from either the modern group shape
 *  (onboardingTemplateId + steps) or the legacy alias shape (templateId +
 *  totalDocuments), exactly the dual shape the source page tolerated. */
interface WorkflowStatus {
  id: string;
  templateId: string;
  name: string;
  progress: number;
  totalDocuments: number;
  statusCounts: StatusCounts;
}

interface OnboardingStatus {
  status: 'not_started' | 'in_progress' | 'completed';
  templateName?: string;
  templateId?: string;
  progress: number;
  totalDocuments: number;
  statusCounts?: StatusCounts;
  requests: DocumentRequest[];
  workflows?: any[]; // raw groups — normalized via normalizeWorkflow()
}

// ---------------------------------------------------------------------------
// Small local helpers (ported from documentService — not part of the
// staffDocumentService contract, so they live here).
// ---------------------------------------------------------------------------

const EXT_TO_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  txt: 'text/plain',
  csv: 'text/csv',
};

const getAcceptAttribute = (types?: string[]): string =>
  (types || [])
    .map((t) => {
      const clean = t.toLowerCase().replace(/^\./, '');
      const parts = [`.${clean}`];
      if (EXT_TO_MIME[clean]) parts.push(EXT_TO_MIME[clean]);
      return parts.join(',');
    })
    .join(',');

const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const TEMPLATE_TYPE_LABEL: Record<string, string> = {
  upload: 'Upload', acknowledgment: 'Acknowledgment', form: 'Web Form', sheet: 'Sheet', fillable_doc: 'Fillable Doc',
};

// Light-chrome badge palette (matches templateTypeMeta light tints).
const TEMPLATE_TYPE_BADGE: Record<string, string> = {
  acknowledgment: 'bg-rose-100 text-rose-700',
  fillable_doc: 'bg-amber-100 text-amber-700',
  form: 'bg-blue-100 text-blue-700',
  sheet: 'bg-teal-100 text-teal-700',
  upload: 'bg-stone-100 text-stone-600',
};

const templateTypeLabel = (t?: string) => TEMPLATE_TYPE_LABEL[t || 'upload'] || 'Upload';
const templateTypeBadge = (t?: string) => TEMPLATE_TYPE_BADGE[t || 'upload'] || TEMPLATE_TYPE_BADGE.upload;

const EMPTY_COUNTS: StatusCounts = { pending: 0, submitted: 0, verified: 0, rejected: 0 };

/** Tolerates both the modern group shape and the legacy alias shape. */
const normalizeWorkflow = (w: any): WorkflowStatus => ({
  id: String(w?.id ?? w?.assignmentId ?? w?.onboardingTemplateId ?? w?.templateId ?? ''),
  templateId: String(w?.templateId ?? w?.onboardingTemplateId ?? ''),
  name: w?.name || 'Document Workflow',
  progress: Number(w?.progress ?? 0),
  totalDocuments: Number(w?.totalDocuments ?? (Array.isArray(w?.steps) ? w.steps.length : 0)),
  statusCounts: { ...EMPTY_COUNTS, ...(w?.statusCounts || {}) },
});

const INPUT_CLASS =
  'w-full min-h-[44px] px-3 py-2.5 rounded-xl border border-stone-300 bg-white text-stone-900 placeholder-stone-400 focus:ring-2 focus:ring-admin-primary focus:border-admin-primary outline-none';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const StaffPortalPage: React.FC = () => {
  const [me, setMe] = useState<StaffMe | null>(null);
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null);
  const [documents, setDocuments] = useState<StaffDocument[]>([]);
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // View state — store the ID, derive fresh data from onboardingStatus.
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);

  // Inline task expansion (no trapping completion popups).
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [signatureName, setSignatureName] = useState<string>('');
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Policy fullscreen modal + scroll-to-end tracking.
  const [policyFullscreenText, setPolicyFullscreenText] = useState<string | null>(null);
  const [policyReadToEnd, setPolicyReadToEnd] = useState(false);

  // Upload inline state
  const [uploadingFor, setUploadingFor] = useState<DocumentRequest | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [expiryDate, setExpiryDate] = useState<string>('');
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  // Acknowledgment inline state
  const [acknowledgeRequest, setAcknowledgeRequest] = useState<DocumentRequest | null>(null);
  const [acknowledgeTemplate, setAcknowledgeTemplate] = useState<DocumentTemplate | null>(null);
  const [acknowledging, setAcknowledging] = useState(false);
  const [acknowledgeChecked, setAcknowledgeChecked] = useState(false);

  // Form inline state
  const [formRequest, setFormRequest] = useState<DocumentRequest | null>(null);
  const [formTemplate, setFormTemplate] = useState<DocumentTemplate | null>(null);
  const [formResponses, setFormResponses] = useState<Record<string, any>>({});
  const [submittingForm, setSubmittingForm] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const workflows = useMemo(
    () => (onboardingStatus?.workflows || []).map(normalizeWorkflow).filter((w) => w.id),
    [onboardingStatus],
  );

  // Derive selectedWorkflow from onboardingStatus so data stays fresh after
  // a refresh; handles both multi-workflow and legacy single-workflow shapes.
  const selectedWorkflow = useMemo<WorkflowStatus | null>(() => {
    if (!selectedWorkflowId || !onboardingStatus) return null;
    const fromWorkflows = workflows.find((w) => w.id === selectedWorkflowId);
    if (fromWorkflows) return fromWorkflows;
    if (selectedWorkflowId === 'legacy' || selectedWorkflowId === onboardingStatus.templateId) {
      return {
        id: onboardingStatus.templateId || 'legacy',
        templateId: onboardingStatus.templateId || 'legacy',
        name: onboardingStatus.templateName || 'Document Workflow',
        progress: onboardingStatus.progress || 0,
        totalDocuments: onboardingStatus.totalDocuments || 0,
        statusCounts: { ...EMPTY_COUNTS, ...(onboardingStatus.statusCounts || {}) },
      };
    }
    return null;
  }, [selectedWorkflowId, onboardingStatus, workflows]);

  const loadData = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setRefreshing(true);
    try {
      const meRes = await staffApi.me().catch(() => null);
      setMe(meRes);
      if (!meRes || !meRes.isRegistered) {
        setOnboardingStatus(null);
        setDocuments([]);
        setTemplates([]);
        return;
      }
      const [onboardingRes, docsRes, templatesRes] = await Promise.all([
        staffPortal.getMyOnboardingStatus().catch(() => null),
        staffPortal.getMyDocuments().catch(() => ({ documents: [], requests: [] })),
        staffDocumentTemplates.getAll().catch(() => []),
      ]);
      setOnboardingStatus((onboardingRes as unknown as OnboardingStatus) || null);
      setDocuments(((docsRes as any)?.documents || []) as StaffDocument[]);
      setTemplates((templatesRes as unknown as DocumentTemplate[]) || []);
    } catch (error: any) {
      console.error('Error loading staff portal data:', error);
      toastService.error(error?.message || 'Failed to load your documents');
    } finally {
      setRefreshing(false);
      setInitialLoadDone(true);
    }
  }, []);

  useEffect(() => { void loadData({ silent: true }); }, [loadData]);

  // Get template metadata for a request
  const getTemplateForRequest = (request: DocumentRequest): DocumentTemplate | undefined =>
    templates.find((t) => t.id === request.documentTemplateId);

  // Toggle inline expansion for a task; primes the right state for its type.
  const toggleTask = useCallback((request: DocumentRequest) => {
    setInlineError(null);
    if (expandedTaskId === request.id) {
      setExpandedTaskId(null);
      return;
    }
    const template = templates.find((t) => t.id === request.documentTemplateId);
    const templateType = template?.templateType || request.templateType || 'upload';

    setExpandedTaskId(request.id);

    if (templateType === 'acknowledgment') {
      setAcknowledgeRequest(request);
      setAcknowledgeTemplate(template || null);
      setAcknowledgeChecked(false);
      setSignatureName('');
    } else if (templateType === 'form') {
      setFormRequest(request);
      setFormTemplate(template || null);
      setFormResponses({});
    } else {
      // upload + fillable_doc both use the upload flow
      const enriched: DocumentRequest = {
        ...request,
        allowedFileTypes: template?.acceptedTypes || ['pdf', 'jpg', 'jpeg', 'png'],
        maxFileSize: request.maxFileSize || 10,
        requiresExpiry: template?.hasExpiry || false,
      };
      setUploadingFor(enriched);
      setSelectedFile(null);
      setExpiryDate('');
      setUploadProgress(0);
    }
  }, [expandedTaskId, templates]);

  // Auto-expand the first actionable (pending/rejected) task when a workflow
  // is opened, so the staff member lands directly on what they need to do.
  useEffect(() => {
    if (!selectedWorkflowId) {
      setExpandedTaskId(null);
      return;
    }
    const templateId =
      workflows.find((w) => w.id === selectedWorkflowId)?.templateId
      ?? onboardingStatus?.templateId;
    const reqs = (onboardingStatus?.requests || []).filter(
      (r) => r.onboardingTemplateId === templateId,
    );
    const firstActionable = reqs.find((r) => r.status === 'pending' || r.status === 'rejected');
    if (firstActionable) toggleTask(firstActionable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkflowId]);

  // ------------------------------------------------------------------
  // Submit handlers
  // ------------------------------------------------------------------

  const handleAcknowledgeSubmit = async () => {
    if (!acknowledgeRequest) return;
    setAcknowledging(true);
    setInlineError(null);
    try {
      await staffPortal.acknowledge(
        acknowledgeRequest.id,
        signatureName.trim() ? { signature: signatureName.trim() } : {},
      );
      toastService.success('Document acknowledged successfully');
      setExpandedTaskId(null);
      setAcknowledgeRequest(null);
      setAcknowledgeTemplate(null);
      setAcknowledgeChecked(false);
      setSignatureName('');
      await loadData({ silent: true });
    } catch (error: any) {
      console.error('Acknowledgment error:', error);
      toastService.error(error?.message || 'Failed to acknowledge document');
    } finally {
      setAcknowledging(false);
    }
  };

  const handleFormSubmit = async () => {
    if (!formRequest || !formTemplate) return;

    const missingFields = (formTemplate.formFields || [])
      .filter((field) => field.required && !formResponses[field.id])
      .map((field) => field.label);
    if (missingFields.length > 0) {
      setInlineError(`Please fill in: ${missingFields.join(', ')}`);
      return;
    }
    setInlineError(null);

    setSubmittingForm(true);
    try {
      // The service wraps this as {responses} — pass the field map itself,
      // NOT {formResponses}, or the backend sees responses.formResponses.
      await staffPortal.submitForm(formRequest.id, formResponses);
      toastService.success('Form submitted successfully');
      setExpandedTaskId(null);
      setFormRequest(null);
      setFormTemplate(null);
      setFormResponses({});
      await loadData({ silent: true });
    } catch (error: any) {
      console.error('Form submit error:', error);
      toastService.error(error?.message || 'Failed to submit form');
    } finally {
      setSubmittingForm(false);
    }
  };

  // ------------------------------------------------------------------
  // File handling
  // ------------------------------------------------------------------

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (file: File) => {
    if (!uploadingFor) return;

    const maxSize = (uploadingFor.maxFileSize || 10) * 1024 * 1024;
    if (file.size > maxSize) {
      toastService.error(`File too large. Maximum size is ${uploadingFor.maxFileSize || 10}MB`);
      return;
    }

    const acceptedTypes = getAcceptAttribute(uploadingFor.allowedFileTypes);
    const fileExt = '.' + (file.name.split('.').pop() || '').toLowerCase();
    const mimeMatch = acceptedTypes.includes(file.type) || acceptedTypes.includes(fileExt);
    if (!mimeMatch && uploadingFor.allowedFileTypes?.length) {
      toastService.error(`Invalid file type. Allowed: ${uploadingFor.allowedFileTypes.join(', ')}`);
      return;
    }

    setSelectedFile(file);
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile || !uploadingFor) return;

    setUploading(true);
    setUploadProgress(0);
    try {
      // 1) Presigned PUT into the private staff-docs bucket.
      const signed = (await staffDocumentUploads.getUploadUrl(
        selectedFile.name,
        selectedFile.type || 'application/octet-stream',
      )) as unknown as { uploadUrl: string; key: string };

      // 2) Browser -> S3 (never through API Gateway).
      setUploadProgress(30);
      await staffDocumentUploads.uploadFile(
        signed.uploadUrl,
        selectedFile,
        selectedFile.type || 'application/octet-stream',
      );

      // 3) Register the submission against the request.
      setUploadProgress(70);
      await staffPortal.submitDocument({
        requestId: uploadingFor.id,
        fileKey: signed.key,
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        fileSize: selectedFile.size,
        expiryDate: expiryDate ? new Date(expiryDate).getTime() : undefined,
      });

      setUploadProgress(100);
      setExpandedTaskId(null);
      setSelectedFile(null);
      setExpiryDate('');
      setUploadingFor(null);

      // Small delay so the backend propagates, then refresh.
      await new Promise((resolve) => setTimeout(resolve, 500));
      await loadData({ silent: true });
      toastService.success('Document uploaded successfully');
    } catch (error: any) {
      console.error('Upload error:', error);
      toastService.error(error?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownloadBlank = async (template?: DocumentTemplate | null) => {
    const key = template?.referenceFileKey;
    if (!key) return;
    try {
      const res = (await staffDocumentUploads.getDownloadUrl(
        key,
        template?.referenceFileName,
      )) as unknown as { url: string };
      window.open(res.url, '_blank');
    } catch (error: any) {
      console.error('Download blank form error:', error);
      toastService.error(error?.message || 'Failed to download blank form');
    }
  };

  const handleViewDocument = async (doc: StaffDocument) => {
    if (!doc.fileKey) {
      toastService.error('Document file not found');
      return;
    }
    try {
      const res = (await staffDocumentUploads.getViewUrl(
        doc.fileKey,
        doc.fileName,
      )) as unknown as { url: string };
      window.open(res.url, '_blank');
    } catch (error: any) {
      console.error('View document error:', error);
      toastService.error(error?.message || 'Failed to open document');
    }
  };

  // ------------------------------------------------------------------
  // Inline completion UI (same controls as the source, dark chrome)
  // ------------------------------------------------------------------

  const renderInlineCompletion = (req: DocumentRequest) => {
    const template = getTemplateForRequest(req);
    const templateType = template?.templateType || req.templateType || 'upload';
    const isRejected = req.status === 'rejected';

    // ---------- ACKNOWLEDGMENT ----------
    if (templateType === 'acknowledgment') {
      const ack = acknowledgeRequest?.id === req.id;
      const policyText = acknowledgeTemplate?.disclaimerText || template?.disclaimerText ||
        'By acknowledging this document, you confirm that you have read and understood its contents and agree to comply with its terms.';
      return (
        <div className="px-3.5 pb-3.5 border-t border-stone-200 pt-3 space-y-3">
          {/* Policy text */}
          <div className="relative">
            <div className="max-h-40 overflow-y-auto text-xs text-stone-800 bg-stone-50 border border-stone-200 rounded-xl p-3 leading-relaxed whitespace-pre-wrap pr-10">
              {policyText}
            </div>
            <button
              type="button"
              onClick={() => { setPolicyReadToEnd(false); setPolicyFullscreenText(policyText); }}
              title="View full policy"
              className="absolute top-2 right-2 p-1.5 rounded-lg bg-white border border-stone-300 text-stone-500 hover:text-stone-900 hover:border-stone-400 transition-colors"
            >
              <Maximize2 size={14} />
            </button>
          </div>
          {/* Read & agree */}
          <label className="flex items-start gap-2 text-sm text-stone-700 cursor-pointer">
            <input
              type="checkbox"
              checked={ack ? acknowledgeChecked : false}
              onChange={(e) => setAcknowledgeChecked(e.target.checked)}
              className="mt-0.5 w-5 h-5"
              style={{ accentColor: RUBY }}
            />
            <span>I have read and agree to the terms and conditions above.</span>
          </label>
          {/* Typed signature */}
          <div>
            <label className="block text-xs font-semibold text-stone-600 mb-1">Type your full name to sign</label>
            <input
              type="text"
              value={ack ? signatureName : ''}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder={me?.name ? `e.g. ${me.name}` : 'e.g. John Carter'}
              className={INPUT_CLASS}
            />
          </div>
          {inlineError && <p className="text-xs text-red-600">{inlineError}</p>}
          <button
            onClick={() => void handleAcknowledgeSubmit()}
            disabled={!acknowledgeChecked || !signatureName.trim() || acknowledging}
            className="w-full min-h-[44px] bg-admin-primary hover:bg-admin-secondary text-white py-2.5 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
          >
            {acknowledging
              ? <><RubyGemMark size={16} drawing color="currentColor" /> Processing...</>
              : <><FileSignature size={18} /> Acknowledge &amp; Sign</>}
          </button>
        </div>
      );
    }

    // ---------- FORM ----------
    if (templateType === 'form') {
      const active = formRequest?.id === req.id;
      const fields = ((active ? formTemplate : template)?.formFields || [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      return (
        <div className="px-3.5 pb-3.5 border-t border-stone-200 pt-3 space-y-3">
          {fields.length === 0 && (
            <div className="text-center py-6 text-stone-500 text-sm">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No form fields configured
            </div>
          )}
          {fields.map((field) => (
            <div key={field.id}>
              {field.type !== 'checkbox' && (
                <label className="block text-xs font-semibold text-stone-600 mb-1">
                  {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
              )}

              {field.type === 'text' && (
                <input type="text" placeholder={field.placeholder || ''} value={formResponses[field.id] || ''}
                  onChange={(e) => setFormResponses((prev) => ({ ...prev, [field.id]: e.target.value }))} className={INPUT_CLASS} />
              )}
              {field.type === 'number' && (
                <input type="text" inputMode="numeric" placeholder={field.placeholder || ''} value={formResponses[field.id] || ''}
                  onChange={(e) => setFormResponses((prev) => ({ ...prev, [field.id]: e.target.value.replace(/[^0-9.\-]/g, '') }))} className={INPUT_CLASS} />
              )}
              {field.type === 'email' && (
                <input type="email" placeholder={field.placeholder || 'email@example.com'} value={formResponses[field.id] || ''}
                  onChange={(e) => setFormResponses((prev) => ({ ...prev, [field.id]: e.target.value }))} className={INPUT_CLASS} />
              )}
              {field.type === 'phone' && (
                <input type="tel" inputMode="tel" placeholder={field.placeholder || '(555) 555-5555'} value={formResponses[field.id] || ''}
                  onChange={(e) => setFormResponses((prev) => ({ ...prev, [field.id]: e.target.value }))} className={INPUT_CLASS} />
              )}
              {field.type === 'textarea' && (
                <textarea rows={3} placeholder={field.placeholder || ''} value={formResponses[field.id] || ''}
                  onChange={(e) => setFormResponses((prev) => ({ ...prev, [field.id]: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-xl border border-stone-300 bg-white text-stone-900 placeholder-stone-400 focus:ring-2 focus:ring-admin-primary focus:border-admin-primary outline-none resize-none" />
              )}
              {field.type === 'select' && (
                <SearchableDropdown
                  options={(field.options || []).map((opt) => ({ value: opt, label: opt }))}
                  value={formResponses[field.id] || null}
                  onChange={(val) => setFormResponses((prev) => ({ ...prev, [field.id]: val || '' }))}
                  placeholder={field.placeholder || 'Select an option...'}
                />
              )}
              {field.type === 'date' && (
                <DatePicker
                  value={formResponses[field.id] || ''}
                  onChange={(val) => setFormResponses((prev) => ({ ...prev, [field.id]: val }))}
                  placeholder={field.placeholder || 'Select a date'}
                />
              )}
              {field.type === 'checkbox' && (
                <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                  <input type="checkbox" checked={formResponses[field.id] || false}
                    onChange={(e) => setFormResponses((prev) => ({ ...prev, [field.id]: e.target.checked }))}
                    className="w-5 h-5" style={{ accentColor: RUBY }} />
                  <span>
                    {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
                    {field.placeholder ? ` — ${field.placeholder}` : ''}
                  </span>
                </label>
              )}
            </div>
          ))}
          {inlineError && <p className="text-xs text-red-600">{inlineError}</p>}
          <button
            onClick={() => void handleFormSubmit()}
            disabled={submittingForm || fields.length === 0}
            className="w-full min-h-[44px] bg-admin-primary hover:bg-admin-secondary text-white py-2.5 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
          >
            {submittingForm
              ? <><RubyGemMark size={16} drawing color="currentColor" /> Submitting...</>
              : <><CheckCircle size={18} /> Submit</>}
          </button>
        </div>
      );
    }

    // ---------- UPLOAD + FILLABLE_DOC ----------
    const isFillable = templateType === 'fillable_doc';
    const up = uploadingFor?.id === req.id ? uploadingFor : null;
    const allowedTypes = up?.allowedFileTypes || template?.acceptedTypes || ['pdf', 'jpg', 'png'];
    return (
      <div className="px-3.5 pb-3.5 border-t border-stone-200 pt-3 space-y-3">
        {/* Fillable: download blank first */}
        {isFillable && template?.referenceFileKey && (
          <button
            onClick={() => void handleDownloadBlank(template)}
            className="w-full min-h-[44px] border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 py-2.5 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            <Download size={16} /> Download blank form
          </button>
        )}

        {/* Accepted types */}
        <div className="flex flex-wrap gap-1.5">
          {allowedTypes.map((type) => (
            <span key={type} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-stone-100 text-stone-600 border border-stone-200">
              {type.toUpperCase()}
            </span>
          ))}
        </div>

        {/* Dropzone */}
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-5 text-center transition-colors bg-stone-50 ${
            dragActive ? 'border-admin-primary bg-red-50' : 'border-stone-300 hover:border-admin-primary'
          }`}
        >
          {selectedFile ? (
            <div className="flex items-center justify-center gap-3">
              <FileText className="w-8 h-8 text-admin-primary" />
              <div className="text-left">
                <p className="font-medium text-stone-900 text-sm">{selectedFile.name}</p>
                <p className="text-xs text-stone-500">{formatFileSize(selectedFile.size)}</p>
              </div>
              <button onClick={() => setSelectedFile(null)} className="p-1 hover:bg-stone-200 rounded">
                <X size={16} className="text-stone-500" />
              </button>
            </div>
          ) : (
            <>
              <Upload className="w-9 h-9 text-stone-400 mx-auto mb-2" />
              <p className="text-sm text-stone-600 mb-2">{isFillable ? 'Upload completed form' : 'Drag & drop your file here'}</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 min-h-[40px] border-2 border-dashed border-admin-primary/50 text-admin-primary hover:bg-red-50 rounded-xl font-semibold text-sm transition-colors"
              >
                Tap to choose file
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept={getAcceptAttribute(allowedTypes)}
                onChange={handleFileSelect}
              />
            </>
          )}
          <p className="text-[11px] text-stone-500 mt-2">Max file size: {up?.maxFileSize || 10}MB</p>
        </div>

        {/* Expiry date */}
        {(up?.requiresExpiry || template?.hasExpiry) && (
          <div>
            <label className="block text-xs font-semibold text-stone-600 mb-1">Expiry Date</label>
            <DatePicker
              value={expiryDate}
              onChange={setExpiryDate}
              placeholder="Select expiry date"
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
        )}

        {/* Progress */}
        {uploading && (
          <div className="space-y-1.5">
            <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%`, background: RUBY }} />
            </div>
            <p className="text-xs text-stone-500 text-center">Uploading... {uploadProgress}%</p>
          </div>
        )}

        <button
          onClick={() => void handleUploadSubmit()}
          disabled={!selectedFile || uploading}
          className="w-full min-h-[44px] bg-admin-primary hover:bg-admin-secondary text-white py-2.5 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
        >
          {uploading
            ? <><RubyGemMark size={16} drawing color="currentColor" /> Uploading...</>
            : <><Upload size={18} /> {isRejected ? 'Re-upload' : 'Upload'}</>}
        </button>
      </div>
    );
  };

  // ------------------------------------------------------------------
  // Shared chrome
  // ------------------------------------------------------------------

  const policyModal = policyFullscreenText !== null && (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Policy document"
    >
      <div className="w-full max-w-2xl max-h-[85vh] flex flex-col bg-white rounded-2xl border border-stone-200 shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 flex-shrink-0 border-b border-stone-200">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-stone-700" />
            <span className="text-sm font-semibold text-stone-900">Policy Document</span>
          </div>
          <button
            type="button"
            onClick={() => setPolicyFullscreenText(null)}
            className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-600 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <div
          className="flex-1 overflow-y-auto p-5 text-sm text-stone-800 bg-stone-50 leading-relaxed whitespace-pre-wrap"
          onScroll={(e) => {
            const el = e.currentTarget;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) setPolicyReadToEnd(true);
          }}
          ref={(el) => {
            // A short policy has nothing to scroll — count that as read.
            if (el && el.scrollHeight <= el.clientHeight + 4 && !policyReadToEnd) setPolicyReadToEnd(true);
          }}
        >
          {policyFullscreenText}
        </div>
        <div className="px-4 py-3 bg-white border-t border-stone-200 flex-shrink-0">
          {policyReadToEnd ? (
            <p className="text-xs text-emerald-600 text-center flex items-center justify-center gap-1.5">
              <CheckCircle size={13} /> You have read to the end — close to sign.
            </p>
          ) : (
            <p className="text-xs text-stone-500 text-center">Scroll to read the full policy, then close to sign.</p>
          )}
        </div>
      </div>
    </div>
  );

  // ------------------------------------------------------------------
  // Loading / not-registered states
  // ------------------------------------------------------------------

  if (!initialLoadDone) {
    return (
      <StaffPage icon={ClipboardCheck} title="My Portal" subtitle="Your onboarding tasks and documents" section="Staff">
        <RubyLoader variant="page" label="Loading your portal…" />
      </StaffPage>
    );
  }

  if (!me || !me.isRegistered) {
    return (
      <StaffPage icon={ClipboardCheck} title="My Portal" subtitle="Your onboarding tasks and documents" section="Staff">
        <div className="max-w-xl mx-auto mt-10 rounded-xl border border-stone-200 bg-white shadow-sm p-10 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-stone-100 flex items-center justify-center mb-5">
            <FolderOpen className="w-8 h-8 text-stone-400" />
          </div>
          <h2 className="text-lg font-semibold text-stone-900 mb-2">You're not registered as a staff member</h2>
          <p className="text-sm text-stone-600">
            {me?.email ? <>Your account (<span className="text-stone-800 font-medium">{me.email}</span>) isn't in the staff directory yet. </> : null}
            Ask a staff manager to invite you — once you're registered, your onboarding tasks and documents will appear here.
          </p>
        </div>
      </StaffPage>
    );
  }

  // Overall stats
  const totalWorkflows = workflows.length || (onboardingStatus && onboardingStatus.status !== 'not_started' ? 1 : 0);
  const pendingTasks = onboardingStatus?.statusCounts?.pending || 0;
  const submittedTasks = onboardingStatus?.statusCounts?.submitted || 0;
  const overallProgress = onboardingStatus?.progress || 0;
  const totalTasks = onboardingStatus?.totalDocuments || 0;
  // From the staff member's perspective, a submitted task is "done" (awaiting review).
  const doneTasks = (onboardingStatus?.statusCounts?.verified || 0) + submittedTasks;

  // ------------------------------------------------------------------
  // WORKFLOW DETAIL VIEW
  // ------------------------------------------------------------------

  if (selectedWorkflow) {
    const workflowRequests = (onboardingStatus?.requests || []).filter(
      (r) => r.onboardingTemplateId === selectedWorkflow.templateId,
    );

    return (
      <StaffPage
        icon={ClipboardList}
        title={selectedWorkflow.name}
        subtitle={`${selectedWorkflow.statusCounts.verified} of ${selectedWorkflow.totalDocuments} documents complete`}
        section="Staff"
        actions={
          selectedWorkflow.progress === 100 ? (
            <span className="px-3 py-1.5 bg-emerald-500 text-white rounded-full text-xs font-semibold flex items-center gap-1.5">
              <CheckCircle size={14} /> Complete
            </span>
          ) : (
            <span className="px-3 py-1.5 bg-white text-black rounded-full text-xs font-semibold">
              {selectedWorkflow.progress}%
            </span>
          )
        }
      >
        <div className="max-w-5xl mx-auto">
          {/* Back to portal */}
          <button
            onClick={() => setSelectedWorkflowId(null)}
            className="mb-4 px-3 py-2 min-h-[40px] rounded-lg bg-white border border-stone-300 text-stone-700 hover:bg-stone-50 text-sm font-medium flex items-center gap-1.5 shadow-sm transition-colors"
            aria-label="Back to portal"
          >
            <ChevronLeft size={16} /> Back to portal
          </button>

          {/* Stat strip + progress */}
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4 mb-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">
                {selectedWorkflow.statusCounts.verified} verified
              </span>
              <span className="px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold">
                {selectedWorkflow.statusCounts.submitted} in review
              </span>
              <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">
                {selectedWorkflow.statusCounts.pending} pending
              </span>
              {selectedWorkflow.statusCounts.rejected > 0 && (
                <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-xs font-semibold">
                  {selectedWorkflow.statusCounts.rejected} rejected
                </span>
              )}
            </div>
            <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden mt-3">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${selectedWorkflow.progress}%`,
                  backgroundColor: selectedWorkflow.progress === 100 ? '#22c55e' : RUBY,
                }}
              />
            </div>
          </div>

          {/* Workflow steps */}
          <div className="space-y-3">
            {workflowRequests.length === 0 && (
              <div className="rounded-xl border border-stone-200 bg-white shadow-sm p-8 text-center text-sm text-stone-500">
                No steps found for this workflow.
              </div>
            )}
            {workflowRequests.map((req, index) => {
              const doc = documents.find((d) => d.requestId === req.id);
              const isComplete = req.status === 'verified';
              const isSubmitted = req.status === 'submitted';
              const isRejected = req.status === 'rejected';
              const isPending = req.status === 'pending';

              const template = getTemplateForRequest(req);
              const templateType = template?.templateType || req.templateType || 'upload';
              const isActionable = isPending || isRejected;
              const isExpanded = expandedTaskId === req.id;
              const isOverdue = req.dueDate ? new Date(req.dueDate) < new Date() : false;

              return (
                <div
                  key={req.id}
                  className={`bg-white rounded-xl overflow-hidden shadow-sm transition-shadow ${
                    isExpanded ? 'border-2 border-admin-primary/70' : 'border border-stone-200'
                  }`}
                >
                  {/* Header row — tap target to expand/collapse */}
                  <button
                    type="button"
                    onClick={() => isActionable && toggleTask(req)}
                    className={`w-full text-left flex items-center gap-3 p-3.5 min-h-[44px] ${
                      isActionable ? 'cursor-pointer hover:bg-stone-50' : 'cursor-default'
                    }`}
                  >
                    {/* Status indicator */}
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${
                      isComplete ? 'bg-emerald-100 text-emerald-700' :
                      isSubmitted ? 'bg-blue-100 text-blue-700' :
                      isRejected ? 'bg-red-100 text-red-600' :
                      'bg-stone-100 text-stone-500'
                    }`}>
                      {isComplete || isSubmitted ? <CheckCircle className="w-4 h-4" /> :
                        isRejected ? <XCircle className="w-4 h-4" /> :
                        (index + 1)}
                    </span>

                    {/* Title & subtitle */}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-stone-900 truncate">{req.templateName}</div>
                      <div className={`text-[11px] truncate ${isExpanded ? 'text-admin-primary font-medium' : 'text-stone-500'}`}>
                        {isComplete ? 'Verified · complete' :
                          isSubmitted ? 'Submitted · in review' :
                          isRejected ? 'Action needed · rejected' :
                          isExpanded ? 'Action needed · complete below' :
                          'Action needed · tap to open'}
                      </div>
                    </div>

                    {/* Type badge */}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide flex-shrink-0 ${templateTypeBadge(templateType)}`}>
                      {templateTypeLabel(templateType)}
                    </span>

                    {/* Chevron for actionable */}
                    {isActionable && (
                      <ChevronDown size={18} className={`text-stone-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    )}
                  </button>

                  {/* Collapsed summary for completed / submitted tasks */}
                  {(isComplete || isSubmitted) && (
                    <div className="px-3.5 pb-3.5 -mt-1 space-y-2">
                      {doc && (
                        <div className="flex items-center gap-2 p-2 bg-stone-50 border border-stone-200 rounded-lg text-xs">
                          {doc.fileType?.startsWith('image/')
                            ? <Image className="w-4 h-4 text-stone-500" />
                            : <FileText className="w-4 h-4 text-stone-500" />}
                          <span className="flex-1 truncate text-stone-700">{doc.fileName}</span>
                          <button onClick={() => void handleViewDocument(doc)} className="p-1.5 rounded hover:bg-stone-200 text-stone-500" title="View">
                            <Eye size={14} />
                          </button>
                        </div>
                      )}
                      {templateType === 'acknowledgment' && req.acknowledgedAt && (
                        <div className="flex items-center gap-2 p-2 bg-emerald-100 rounded-lg text-xs text-emerald-800">
                          <CheckCircle className="w-4 h-4" />
                          <span>
                            Acknowledged on {new Date(req.acknowledgedAt).toLocaleDateString()}
                            {req.signature ? ` · signed "${req.signature}"` : ''}
                          </span>
                        </div>
                      )}
                      {templateType === 'form' && req.formResponses && (
                        <div className="flex items-center gap-2 p-2 bg-emerald-100 rounded-lg text-xs text-emerald-800">
                          <ClipboardCheck className="w-4 h-4" />
                          <span>Form completed · {Object.keys(req.formResponses).length} responses</span>
                        </div>
                      )}
                      {templateType === 'fillable_doc' && template?.referenceFileKey && (
                        <button
                          onClick={() => void handleDownloadBlank(template)}
                          className="w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                        >
                          <Download size={16} /> Download blank form
                        </button>
                      )}
                    </div>
                  )}

                  {/* Inline expanded completion (actionable + expanded) */}
                  {isActionable && isExpanded && (
                    <>
                      {/* Due date / rejection / notes context */}
                      {(req.dueDate || (isRejected && req.rejectionReason) || req.notes) && (
                        <div className="px-3.5 pb-1 space-y-1.5">
                          {req.dueDate && (
                            <p className={`text-xs flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : 'text-stone-500'}`}>
                              <Calendar size={12} /> Due: {new Date(req.dueDate).toLocaleDateString()}{isOverdue && ' · Overdue'}
                            </p>
                          )}
                          {isRejected && req.rejectionReason && (
                            <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                              <strong>Reason:</strong> {req.rejectionReason}
                            </div>
                          )}
                          {req.notes && (
                            <p className="text-xs text-stone-500 italic">Note: {req.notes}</p>
                          )}
                        </div>
                      )}
                      {renderInlineCompletion(req)}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {policyModal}
      </StaffPage>
    );
  }

  // ------------------------------------------------------------------
  // MAIN PORTAL VIEW
  // ------------------------------------------------------------------

  return (
    <StaffPage
      icon={ClipboardCheck}
      title="My Portal"
      subtitle="Your onboarding tasks and documents"
      section="Staff"
      actions={
        <button
          onClick={() => void loadData()}
          disabled={refreshing}
          className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 min-h-[40px] rounded-lg text-xs md:text-sm font-medium transition-colors flex items-center gap-2 flex-shrink-0 disabled:opacity-50"
        >
          {refreshing ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw size={16} />}
          <span className="hidden md:inline">Refresh</span>
        </button>
      }
    >
      <div className="max-w-5xl mx-auto">
        {/* Greeting + progress overview */}
        <div className="mb-4">
          <h2 className="text-lg md:text-xl font-bold text-stone-900">Welcome, {me.name || me.staff?.name || 'there'}</h2>
          {me.role?.name && (
            <p className="text-xs text-stone-500 mt-0.5">
              {me.staff?.title ? `${me.staff.title} · ` : ''}{me.role.name}
            </p>
          )}
          {totalWorkflows > 0 && (
            <div className="mt-2 bg-white rounded-xl border border-stone-200 shadow-sm p-3 flex items-center gap-3">
              <div className="relative w-12 h-12 flex-shrink-0">
                <svg viewBox="0 0 36 36" className="w-12 h-12">
                  <circle cx="18" cy="18" r="15" fill="none" stroke="#e7e5e4" strokeWidth="4" />
                  <circle
                    cx="18" cy="18" r="15" fill="none" stroke={RUBY} strokeWidth="4"
                    strokeDasharray={2 * Math.PI * 15}
                    strokeDashoffset={(2 * Math.PI * 15) * (1 - overallProgress / 100)}
                    strokeLinecap="round" transform="rotate(-90 18 18)"
                    style={{ transition: 'stroke-dashoffset .5s' }}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-stone-900">{overallProgress}%</span>
              </div>
              <div className="text-sm min-w-0">
                <div className="font-semibold text-stone-900">{doneTasks} of {totalTasks} tasks done</div>
                <div className="text-stone-500 text-xs">
                  {totalWorkflows} workflow{totalWorkflows === 1 ? '' : 's'} assigned
                  {pendingTasks > 0 && ` · ${pendingTasks} pending`}
                  {submittedTasks > 0 && ` · ${submittedTasks} in review`}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* No workflows assigned */}
        {(!onboardingStatus || onboardingStatus.status === 'not_started') && (
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-12 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-stone-100 flex items-center justify-center mb-6">
              <FolderOpen className="w-10 h-10 text-stone-400" />
            </div>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">No Workflows Assigned Yet</h2>
            <p className="text-stone-600 max-w-md mx-auto text-sm">
              No document workflows have been assigned to you yet.
              You'll receive an email notification when new tasks are available.
            </p>
          </div>
        )}

        {/* All-complete banner */}
        {onboardingStatus && onboardingStatus.progress === 100 && totalWorkflows > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl shadow-sm p-6 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-emerald-900">All Tasks Complete!</h2>
                <p className="text-emerald-700">Congratulations! You've completed all assigned workflows.</p>
              </div>
            </div>
          </div>
        )}

        {/* Workflows list */}
        {workflows.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-1">Your workflows</h2>

            {workflows.map((workflow) => {
              const isComplete = workflow.progress === 100;
              const hasRejected = workflow.statusCounts.rejected > 0;
              const wfRequests = (onboardingStatus?.requests || []).filter((r) => r.onboardingTemplateId === workflow.templateId);
              const doneCount = workflow.statusCounts.verified;
              const totalCount = workflow.totalDocuments;
              const nextTask = wfRequests.find((r) => r.status === 'pending' || r.status === 'rejected');

              return (
                <button
                  key={workflow.id}
                  type="button"
                  onClick={() => setSelectedWorkflowId(workflow.id)}
                  className="w-full text-left bg-white rounded-xl border border-stone-200 shadow-sm p-4 min-h-[44px] hover:border-stone-300 hover:shadow transition"
                >
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-semibold text-stone-900 truncate">{workflow.name}</h3>
                    {isComplete ? (
                      <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wide flex items-center gap-1">
                        <CheckCircle size={11} /> Complete
                      </span>
                    ) : hasRejected ? (
                      <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-[10px] font-bold uppercase tracking-wide">
                        Action needed
                      </span>
                    ) : (
                      <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wide">
                        In progress
                      </span>
                    )}
                  </div>

                  {/* Progress bar */}
                  <div className="h-2 bg-stone-200 rounded-full mt-2 overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${workflow.progress}%`, background: RUBY }} />
                  </div>

                  <p className="text-xs text-stone-500 mt-1.5">
                    {doneCount} of {totalCount} done
                    {!isComplete && nextTask && <span> · next: {nextTask.templateName}</span>}
                  </p>
                </button>
              );
            })}
          </div>
        )}

        {/* Single workflow (backward compatibility) */}
        {onboardingStatus && onboardingStatus.status !== 'not_started' && workflows.length === 0 && (
          <div className="space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-1">Your workflow</h2>

            <button
              type="button"
              onClick={() => setSelectedWorkflowId(onboardingStatus.templateId || 'legacy')}
              className="w-full text-left bg-white rounded-xl border border-stone-200 shadow-sm p-4 min-h-[44px] hover:border-stone-300 hover:shadow transition"
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold text-stone-900 truncate">{onboardingStatus.templateName || 'Document Workflow'}</h3>
                {onboardingStatus.progress === 100 ? (
                  <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wide flex items-center gap-1">
                    <CheckCircle size={11} /> Complete
                  </span>
                ) : (
                  <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wide">
                    In progress
                  </span>
                )}
              </div>
              <div className="h-2 bg-stone-200 rounded-full mt-2 overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${onboardingStatus.progress}%`, background: RUBY }} />
              </div>
              <p className="text-xs text-stone-500 mt-1.5">{onboardingStatus.progress}% complete</p>
            </button>
          </div>
        )}

        {/* My documents archive */}
        {documents.length > 0 && (
          <div className="mt-8 space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-1">My documents</h2>
            <div className="rounded-xl border border-stone-200 bg-white shadow-sm divide-y divide-stone-200 overflow-hidden">
              {documents
                .slice()
                .sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0))
                .map((doc) => {
                  const isExpired = doc.expiryDate ? doc.expiryDate < Date.now() : false;
                  return (
                    <div key={doc.id} className="flex items-center gap-3 px-4 py-3">
                      <span className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                        {doc.fileType?.startsWith('image/')
                          ? <Image className="w-4 h-4 text-stone-500" />
                          : <FileText className="w-4 h-4 text-stone-500" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-900 truncate">{doc.documentName || doc.fileName}</p>
                        <p className="text-[11px] text-stone-500 truncate">
                          {doc.fileName}
                          {doc.fileSize ? ` · ${formatFileSize(doc.fileSize)}` : ''}
                          {doc.uploadedAt ? ` · uploaded ${new Date(doc.uploadedAt).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                      {doc.expiryDate && (
                        <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                          isExpired ? 'bg-red-100 text-red-700' : 'bg-stone-100 text-stone-600'
                        }`}>
                          {isExpired ? 'Expired' : `Expires ${new Date(doc.expiryDate).toLocaleDateString()}`}
                        </span>
                      )}
                      <button
                        onClick={() => void handleViewDocument(doc)}
                        className="flex-shrink-0 p-2 min-h-[40px] min-w-[40px] flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-500 hover:text-stone-900 transition-colors"
                        title="View document"
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {policyModal}
    </StaffPage>
  );
};

export default StaffPortalPage;
